/* eslint-disable complexity */
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { useCreateRotationsplanVorlage } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateRotationsplanVorlage/useCreateRotationsplanVorlage';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useForm as useTanStackForm } from '@tanstack/react-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

const VORLAGEN_PATH = '/mitarbeiter/rotationsplaene/vorlagen';

export function useForm() {
  const [createRotationsplanVorlage] = useCreateRotationsplanVorlage();
  const { currentStandort } = useStandortContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Fehler vom Server, gilt nur bis zur nächsten Änderung im Formular
  const [titelVergeben, setTitelVergeben] = useState(false);

  const schema = z.object({
    beruf: z.instanceof(Beruf, {
      message: t('generalValidationError.required'),
    }),
    titel: z
      .string()
      .trim()
      .min(1, {
        message: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.titel.validationError',
        ),
      }),
  });

  type FormValues = z.infer<typeof schema>;

  async function handleSubmit(value: FormValues) {
    const showError = () =>
      toast({
        variant: 'destructive',
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.error',
        ),
      });

    try {
      const result = await createRotationsplanVorlage({
        input: {
          berufId: value.beruf.id,
          titel: value.titel.trim(),
          standortId: currentStandort.uid,
        },
      });
      const response = result.data?.mitarbeiter_createRotationsplanVorlage;

      if (
        response?.error?.__typename ===
        'mitarbeiter_rotationsplanVorlageTitelVergeben'
      ) {
        setTitelVergeben(true);
        return;
      }

      if (!response?.success) {
        showError();
        return;
      }

      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.success',
        ),
      });
      // direkt in die neue Vorlage, damit Abschnitte und Stationen ohne Umweg angelegt werden können
      navigate(`${VORLAGEN_PATH}/${response.success.rotationsplanVorlage.id}`, {
        replace: true,
      });
    } catch (error) {
      Sentry.captureException(error);
      showError();
    }
  }

  const form = useTanStackForm({
    defaultValues: {
      beruf: undefined as unknown as Beruf,
      titel: '',
    },
    validators: {
      onChange: schema,
    },
    listeners: {
      onChange: () => setTitelVergeben(false),
    },
    onSubmit: async ({ value }) => await handleSubmit(value),
  });

  return { form, titelVergeben };
}
